import React, { useEffect, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { billingService, getErrorMessage } from '../services/api';
import type { CheckoutPlan } from '../utils/checkout';
import { useToast } from './Toast';

// While online payment is pending (PLAN-18; Paddle not configured), a buy button becomes this: a short note and "Request a
// free month" of the plan, for the user (Student plan) or for one of the teacher's classes (Class plan). The request is an
// email to the owner, who grants the month in the admin dashboard (PLAN-9, PLAN-19). Once sent (or already sent today),
// the answer replaces the button for the rest of the browser session, wherever the same offer shows.
// Remove with PLAN-18 at go-live (docs/DEPLOYMENT.md, "Go-live checklist").

export const TRIAL_NOTE = "We're finishing online payments. Until then, you can get a free month.";

const SENT_EVENT = 'webots-trial-requested';
const sentKey = (plan: CheckoutPlan, classId?: number) => `webots-trial-requested:${plan}:${classId ?? ''}`;

function readSent(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function rememberSent(key: string, message: string) {
  try {
    window.sessionStorage.setItem(key, message);
  } catch {
    // Private mode or storage off: the answer still shows until the page is left.
  }
  // The same offer elsewhere on the page (the account menu over the plan line) shows the answer too.
  window.dispatchEvent(new CustomEvent(SENT_EVENT, { detail: { key, message } }));
}

const TrialRequest: React.FC<{
  plan: CheckoutPlan;
  forClass?: { id: number; name: string };
  // Shown before the note: what the plan gives (the buy button said it).
  offer?: React.ReactNode;
  // `span` where it sits inside a paragraph (PlanLine).
  as?: 'div' | 'span';
  className?: string;
  noteClassName?: string;
  buttonClassName?: string;
  doneClassName?: string;
  icon?: React.ReactNode;
}> = ({ plan, forClass, offer, as: Tag = 'div', className, noteClassName, buttonClassName, doneClassName, icon }) => {
  const toast = useToast();
  const key = sentKey(plan, forClass?.id);
  const [sent, setSent] = useState<string | null>(() => readSent(key));
  const [sending, setSending] = useState(false);
  useEffect(() => {
    const onSent = (event: Event) => {
      const { detail } = event as CustomEvent<{ key: string; message: string }>;
      if (detail.key === key) setSent(detail.message);
    };
    window.addEventListener(SENT_EVENT, onSent);
    return () => window.removeEventListener(SENT_EVENT, onSent);
  }, [key]);
  const Line = Tag === 'span' ? 'span' : 'p';
  const request = async () => {
    setSending(true);
    try {
      const message = await billingService.requestTrial(plan, forClass?.id);
      rememberSent(key, message);
      setSent(message);
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 429 || status === 409) {
        // Already asked today, or nothing to ask for any more: the server's words, and no button to press again.
        const message = getErrorMessage(error);
        rememberSent(key, message);
        setSent(message);
      } else {
        toast.error(getErrorMessage(error, 'The request could not be sent. Please try again.'));
      }
    } finally {
      setSending(false);
    }
  };
  return (
    <Tag className={className} data-testid="trial-request">
      {offer && <Line className="block">{offer}</Line>}
      <Line className={`block ${noteClassName ?? 'text-sm text-gray-600 dark:text-gray-400'}`}>{TRIAL_NOTE}</Line>
      {sent ? (
        <Line role="status" className={`mt-2 flex items-start gap-1.5 font-medium ${doneClassName ?? 'text-emerald-700 dark:text-emerald-400'}`} data-testid="trial-request-sent">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" /> {sent}
        </Line>
      ) : (
        <button
          type="button"
          onClick={request}
          disabled={sending}
          aria-busy={sending}
          className={`${buttonClassName ?? 'btn btn-primary'} mt-2 disabled:opacity-70 disabled:cursor-wait`}
        >
          {icon}{icon ? ' ' : ''}Request a free month
        </button>
      )}
    </Tag>
  );
};

export default TrialRequest;
